/**
 * Округление наличного итога КП для МСК Газобетона.
 *
 * Работает только при одновременном совпадении двух условий: включена область
 * МСК Газобетона и выбрана оплата наличными. Во всех остальных случаях — безналичный
 * расчёт, СПБ, кирпич, другие направления и города — итог остаётся прежним.
 *
 * Округляется ровно один раз и только окончательная сумма КП, уже посчитанная из
 * материалов и доставки. Цены за единицу, отдельные товары, доставка и любые
 * промежуточные суммы не трогаются: сюда приходит готовый итог выбранного
 * предложения, своего расчёта здесь нет.
 */

export const CASH_ROUNDING_STEP = 500;

const CASH_PAYMENT_METHOD = 'cash';

/**
 * @param {object} params
 * @param {number} params.total                 — итог КП (материалы + доставка)
 * @param {boolean} params.isMskGazobetonScope  — признак области, приходит готовым
 * @param {string} params.paymentMethod         — форма оплаты выбранного предложения
 * @returns {{ applies: boolean, total: number|null, difference: number }}
 *   applies    — округление сработало;
 *   total      — сумма к оплате (округлённая, либо исходная);
 *   difference — насколько изменился итог: положительное вверх, отрицательное вниз.
 */
export const resolveGazCashTotal = ({ total, isMskGazobetonScope, paymentMethod } = {}) => {
  // null, undefined и пустая строка — это «итога нет», а не ноль рублей:
  // Number(null) вернул бы 0 и показал бы сумму, которой не было.
  if (total === null || total === undefined || total === '') {
    return { applies: false, total: null, difference: 0 };
  }

  const sourceTotal = Number(total);

  if (!Number.isFinite(sourceTotal)) {
    return { applies: false, total: null, difference: 0 };
  }

  if (!isMskGazobetonScope || paymentMethod !== CASH_PAYMENT_METHOD) {
    return { applies: false, total: sourceTotal, difference: 0 };
  }

  const rounded = Math.round(sourceTotal / CASH_ROUNDING_STEP) * CASH_ROUNDING_STEP;
  // Разницу считаем в копейках и возвращаем к рублям: иначе на дробных итогах
  // всплывает мусор вроде 61.99999999999.
  const difference = Math.round((rounded - sourceTotal) * 100) / 100;

  return { applies: true, total: rounded, difference };
};

/** «Округление наличными: +62 ₽». null — если сумма уже кратна шагу. */
export const buildGazCashRoundingNote = ({ applies, difference } = {}) => {
  if (!applies || !difference) {
    return null;
  }

  const sign = difference > 0 ? '+' : '−';
  const value = Math.abs(difference).toLocaleString('ru-RU', { maximumFractionDigits: 2 });
  return `Округление наличными: ${sign}${value} ₽`;
};

export default resolveGazCashTotal;
